import { createSelector } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';

import { InsightsState } from 'app-redux/InsightsState';
import { parseIsoDateStartOfDayUtc0 } from 'utils/dateTimeHelpers';

import { GetSelectedRisk } from 'components/RisksProjectionCard/RisksProjectionCard';

import diseaseActivitiesTimeFrameSelectors from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.selectors';
import diseaseActivityListSelectors from './diseaseActivityList.selectors';

const diseaseActivityDetails = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.disacDetailsData;
const isLoading = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.disacDetailsIsLoading;
const isLoadingWrtPreviousPanel = createSelector(
  isLoading,
  diseaseActivityListSelectors.isLoadingWrtPreviousPanel,
  (isLoadingEDP, isLoadingWrtPreviousPanelDELP) => isLoadingEDP || isLoadingWrtPreviousPanelDELP
);
const hasError = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.disacDetailsHasError;
const countryName = createSelector(diseaseActivityDetails, x => x?.eventInformation?.countryName);
const eventTitle = createSelector(diseaseActivityDetails, x => x?.eventInformation?.title);
const eventStartDate = createSelector(diseaseActivityDetails, x => x?.eventInformation?.startDate);
const lastUpdatedDate = createSelector(diseaseActivityDetails, x => x?.eventInformation?.lastUpdatedDate);
const firstActivityUpdateDate = createSelector(diseaseActivityDetails, x => x?.eventInformation?.firstActivityUpdateDate);
const caseCounts = createSelector(diseaseActivityDetails, x => x?.caseCounts);
const eventLocations = createSelector(diseaseActivityDetails, x => x?.eventLocations);
const importationRisk = createSelector(diseaseActivityDetails, x => x?.importationRisk);
const exportationRisk = createSelector(diseaseActivityDetails, x => x?.exportationRisk);
const outbreakPotentialCategory = createSelector(diseaseActivityDetails, x => x?.outbreakPotentialCategory);
const diseaseInformation = createSelector(diseaseActivityDetails, x => x?.diseaseInformation);
const airports = createSelector(diseaseActivityDetails, x => x?.airports);
const sourceAirports = createSelector(diseaseActivityDetails, x => x?.airports?.sourceAirports || []);
const destinationAirports = createSelector(diseaseActivityDetails, x => x?.airports?.destinationAirports || []);
const isLocal = createSelector(diseaseActivityDetails, x => x?.isLocal);
const articles = createSelector(diseaseActivityDetails, diseaseActivitiesTimeFrameSelectors.timeframeStartDateAsDate, (disact, startDate) =>
  orderBy(
    disact?.articles?.filter(article => parseIsoDateStartOfDayUtc0(article.publishedDate) >= startDate) || [],
    article => new Date(article.publishedDate),
    'desc'
  )
);
const calculationMetadata = createSelector(diseaseActivityDetails, x => x?.calculationMetadata);
const selectedRiskType = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.selectedRiskType;
const selectedRisk = createSelector(diseaseActivityDetails, selectedRiskType, GetSelectedRisk);
const getSelectedEventLocations = createSelector(diseaseActivityDetails, isLoading, (det, loading) => (loading ? null : det?.eventLocations));
const getSelectedEventAirports = createSelector(diseaseActivityDetails, isLoading, (det, loading) => (loading ? null : det?.airports));

// diseaseActivityDetailsSelectors
export default {
  isLoading,
  isLoadingWrtPreviousPanel,
  hasError,
  diseaseActivityDetails,
  countryName,
  eventTitle,
  eventStartDate,
  lastUpdatedDate,
  caseCounts,
  eventLocations,
  importationRisk,
  exportationRisk,
  outbreakPotentialCategory,
  diseaseInformation,
  airports,
  sourceAirports,
  destinationAirports,
  articles,
  calculationMetadata,
  selectedRiskType,
  selectedRisk,
  firstActivityUpdateDate,
  isLocal,
  getSelectedEventLocations,
  getSelectedEventAirports,
};
